import { Minus, Plus } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  AddButton,
  BackButton,
  CustomCheckbox,
  SubmitButton,
  ThinDropdownWithSearch,
  ThinTextField,
  ToggleButton,
} from "../../common/GeneralComponent";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchData } from "../../../utils/functions";
import Permissions from "../permissions/Permissions";

const AddRoles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [groupsData, setGroupsData] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch groups
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetchData(
          "groups",
          "GET",
          null,
          {},
          {},
          navigate,
          "/groups",
          dispatch
        );
        setGroupsData(response?.data || []);
      } catch (error) {
        console.error("Error fetching groups:", error);
      }
    };

    fetchGroups();
  }, [navigate, dispatch]);

  const validationSchema = Yup.object({
    role_name: Yup.string()
      .required("Role name is required")
      .min(3, "Role name must be at least 3 characters")
      .max(50, "Role name must not exceed 50 characters")
      .matches(/^[^\d]/, "Role name cannot start with a number")
      .matches(/^[a-zA-Z0-9 ]*$/, "Role name must not contain special characters")
      .test(
        "not-only-numbers",
        "Role name cannot be only numbers",
        (value) => value && !/^\d+$/.test(value)
      ),
    groupId: Yup.string().required("Group is required"),
    status: Yup.string().required("Status is required"),
    permissionIds: Yup.array()
      .min(1, "At least one permission is required.")
      .required("Permissions are required"),
  });

  const formik = useFormik({
    initialValues: {
      role_name: "",
      groupId: "",
      status: "1",
      permissionIds: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      const data = {
        role_name: values.role_name,
        groupId: values.groupId,
        status: values.status,
        permissionIds: selectedPermissions,
      };

      try {
        await fetchData("role", "POST", data);
        toast.success("Role created successfully");
        navigate("/roles");
        setIsSubmitting(true);
      } catch (error) {
        toast.error(`Error: ${error.message}`);
      }
    },
  });

  // Sync selected permissions with Formik
  useEffect(() => {
    formik.setFieldValue("permissionIds", selectedPermissions);
  }, [selectedPermissions]);

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Create Roles
        </h1>
        <BackButton to="/roles" text="Back" />
      </div>

      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Create Roles
          </h1>
        </div>

        <form onSubmit={formik.handleSubmit} className="p-4">
          <div className="flex lg:flex-nowrap flex-wrap gap-5 w-full">
            <ThinDropdownWithSearch
              label="Group Name"
              id="groupId"
              options={groupsData.map((group) => ({
                value: group.id,
                label: group.name,
                permissions: group.permissions,
              }))}
              value={formik.values.groupId}
              onChange={(selectedOption) => {
                formik.setFieldValue("groupId", selectedOption);
                setSelectedGroup(selectedOption);
              }}
              helperText={formik.touched.groupId && formik.errors.groupId}
              error={formik.touched.groupId && formik.errors.groupId}
              required
            />

            <ThinTextField
              id="role_name"
              label="Role Name"
              placeholder="Enter Role Name"
              type="text"
              value={formik.values.role_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.role_name && formik.errors.role_name}
              helperText={formik.touched.role_name && formik.errors.role_name}
            />

            <ThinDropdownWithSearch
              label="Status"
              id="status-dropdown"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.status}
              onChange={(value) =>
                formik.setFieldValue("status", Number(value))
              }
              onBlur={formik.handleBlur}
              error={formik.touched.status && Boolean(formik.errors.status)}
              helperText={formik.touched.status && formik.errors.status}
            />
          </div>

          <Permissions
            selectedGroup={selectedGroup}
            selectedPermissions={selectedPermissions}
            setSelectedPermissions={setSelectedPermissions}
          />

          {formik.touched.permissionIds && formik.errors.permissionIds && (
            <p className="mt-2 text-sm text-red-600">
              {formik.errors.permissionIds}
            </p>
          )}

          <SubmitButton type="submit" text="Submit" isLoading={isSubmitting} />
        </form>
      </div>
    </div>
  );
};

export default AddRoles;
